import { useEffect, useId, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { LifeBuoy } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { apiFetch } from '../../utils/api';

const CHOICES = [
  { id: 'bug', label: "Something's broken" },
  { id: 'question', label: 'A question' },
  { id: 'feedback', label: 'Feedback' },
] as const;

type Category = (typeof CHOICES)[number]['id'];

const PLACEHOLDER: Record<Category, string> = {
  bug: 'What broke, and what were you trying to do?',
  question: 'What do you need?',
  feedback: 'What should we change?',
};

/**
 * Closed help control. Chip matches the top bar. Icon matches Odin's close button.
 * The panel hangs from the control. It never sits on the page.
 */
export default function SupportHelp({ variant = 'chip' }: { variant?: 'chip' | 'icon' }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<Category>('bug');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open]);

  const send = async () => {
    const text = message.trim();
    if (!text || sending) return;
    setSending(true);
    setError('');
    try {
      const stored = category === 'feedback'
        ? apiFetch('/api/feedback', {
          method: 'POST',
          body: { message: text, source: 'help', route: location.pathname },
        }).then((r) => r.ok).catch(() => false)
        : Promise.resolve(false);
      const response = await apiFetch('/api/support/message', {
        method: 'POST',
        body: { category, message: text, path: location.pathname },
      });
      const data = await response.json().catch(() => ({}));
      if ((!response.ok || data?.ok === false) && !(await stored)) {
        setError(typeof data?.error === 'string' ? data.error : 'Help did not send. Try again in a minute.');
        return;
      }
      setSent(true);
      setMessage('');
    } catch {
      setError('Help did not send. Try again in a minute.');
    } finally {
      setSending(false);
    }
  };

  const buttonClass = variant === 'chip'
    ? 'inline-flex items-center gap-1.5 h-9 px-3 rounded-btn border border-neutral-90/15 bg-white text-sm text-neutral-90 hover:bg-neutral-90/[0.04] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-90'
    : 'shrink-0 p-2 rounded-btn text-neutral-90/40 hover:text-neutral-90 hover:bg-neutral-90/5 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-90';

  return (
    <div className="relative" ref={panelRef}>
      <button
        ref={buttonRef}
        type="button"
        data-testid="support-help-open"
        aria-expanded={open}
        aria-haspopup="dialog"
        title="Help"
        onClick={() => {
          setOpen((value) => !value);
          setSent(false);
          setError('');
        }}
        className={buttonClass}
      >
        <LifeBuoy size={variant === 'chip' ? 16 : 18} className={variant === 'chip' ? 'text-neutral-90' : undefined} />
        {variant === 'chip' && <span className="max-lg:hidden">Help</span>}
        {variant === 'icon' && <span className="sr-only">Help</span>}
      </button>
      <AnimatePresence>
        {open && (
          <SupportPanel
            titleId={titleId}
            category={category}
            message={message}
            sending={sending}
            error={error}
            sent={sent}
            onCategory={setCategory}
            onMessage={setMessage}
            onSend={() => { void send(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function SupportPanel({
  titleId,
  category,
  message,
  sending,
  error,
  sent,
  onCategory,
  onMessage,
  onSend,
}: {
  titleId: string;
  category: Category;
  message: string;
  sending: boolean;
  error: string;
  sent: boolean;
  onCategory: (category: Category) => void;
  onMessage: (value: string) => void;
  onSend: () => void;
}) {
  return (
    <motion.div
      role="dialog"
      aria-labelledby={titleId}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.15 }}
      className="absolute right-0 top-full z-[60] mt-2 w-80 origin-top-right overflow-hidden rounded-xl border border-neutral-90/10 bg-white shadow-xl selection:bg-neutral-90/15 max-lg:fixed max-lg:inset-x-3 max-lg:top-16 max-lg:mt-0 max-lg:w-auto"
    >
      <h2 id={titleId} className="border-b border-neutral-90/10 px-4 py-3 text-sm font-semibold text-neutral-90">Tell us what happened</h2>
      <div className="p-4">
      <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Kind of message">
        {CHOICES.map((choice) => {
          const selected = choice.id === category;
          return (
            <button
              key={choice.id}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onCategory(choice.id)}
              className={`rounded-btn px-2.5 py-1.5 text-xs font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-90 ${
                selected ? 'bg-neutral-90 text-white' : 'text-neutral-90/60 hover:bg-neutral-90/5 hover:text-neutral-90'
              }`}
            >
              {choice.label}
            </button>
          );
        })}
      </div>
      {sent ? (
        <p className="mt-4 text-sm text-neutral-90">Sent. We will follow up by email.</p>
      ) : (
        <>
          <label className="mt-3 block">
            <span className="sr-only">Message</span>
            <textarea
              value={message}
              onChange={(event) => onMessage(event.target.value)}
              placeholder={PLACEHOLDER[category]}
              rows={4}
              maxLength={4000}
              className="w-full resize-none rounded-btn border border-neutral-90/15 bg-white px-3 py-2 text-sm text-neutral-90 placeholder:text-neutral-90/55 caret-neutral-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-90"
            />
          </label>
          {error && <p className="mt-2 text-xs text-red-700">{error}</p>}
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={onSend}
              disabled={sending || !message.trim()}
              className="rounded-btn bg-neutral-90 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-neutral-90/85 disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-90"
            >
              {sending ? 'Sending…' : 'Send'}
            </button>
          </div>
        </>
      )}
      </div>
    </motion.div>
  );
}
